import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from "@hello-pangea/dnd";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

import {
  AssessmentReportTableColumn,
  AssessmentReportViewOptions,
} from "./types";

const readonlyColumns: (string | number)[] = [
  "name",
  "status",
  "manager-status",
];

const TableColumnHider = ({
  viewOptionsTableColumns,
  sortedTableColumns,
  hiddenRecentAnswers = [],
  recentAnswersQuestionIds = [],
  onDragEnd,
  onToggleColumnVisibility,
  onUpdateViewOptions,
}: {
  viewOptionsTableColumns: {
    id: string | number;
    hidden: boolean;
  }[];
  sortedTableColumns: AssessmentReportTableColumn[];
  hiddenRecentAnswers?: number[];
  recentAnswersQuestionIds?: number[];
  onDragEnd: OnDragEndResponder;
  onToggleColumnVisibility: (colId: string | number, hidden: boolean) => void;
  onUpdateViewOptions: (opts: Partial<AssessmentReportViewOptions>) => void;
}) => {
  const label = useLabel();
  console.log({ recentAnswersQuestionIds });
  return (
    <div className="border-t p-6">
      <div className="font-semibold mb-4">Table Columns</div>
      {recentAnswersQuestionIds.length > 0 && (
        <div className="font-medium mb-2">{`From This ${label("review", {
          capitalize: true,
        })}`}</div>
      )}
      <div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {sortedTableColumns.map((column, index) => {
                  const viewColumn = viewOptionsTableColumns.find(
                    (col) => col.id === column.id,
                  );
                  const isHidden = viewColumn ? viewColumn.hidden : false;
                  return (
                    <Draggable
                      key={column.id}
                      draggableId={`${column.id}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          key={column.id}
                          ref={provided.innerRef}
                          role="button"
                          className="flex items-center justify-between p-2 rounded-md hover:cursor-pointer hover:bg-gray-100"
                          {...provided.draggableProps}
                        >
                          <div
                            className="flex items-center gap-1"
                            {...provided.dragHandleProps}
                          >
                            <TopicflowIcon
                              type="drag"
                              className="h-5 w-5 text-gray-400"
                            />
                            <div>{column.label}</div>
                          </div>
                          {!readonlyColumns.includes(column.id) && (
                            <Button
                              icon={
                                !isHidden
                                  ? TopicflowIconMap.eye
                                  : TopicflowIconMap.eyeOff
                              }
                              theme={buttonTheme.iconGray}
                              className={classNames(
                                !isHidden && "!text-gray-800",
                              )}
                              size={ButtonSize.medium}
                              onClick={() => {
                                onToggleColumnVisibility(column.id, !isHidden);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {recentAnswersQuestionIds.length > 0 && (
        <>
          <div className="font-medium mt-4 mb-2">{`From a Previous ${label(
            "review",
            { capitalize: true },
          )}`}</div>
          <div>
            {recentAnswersQuestionIds.map((questionId) => {
              const col = sortedTableColumns.find((c) => c.id === questionId);
              const isHidden = hiddenRecentAnswers.includes(questionId);
              if (!col) {
                return null;
              }
              return (
                <div
                  key={questionId}
                  className="flex items-center justify-between p-2"
                >
                  <div>{col.label}</div>
                  <Button
                    icon={
                      !isHidden ? TopicflowIconMap.eye : TopicflowIconMap.eyeOff
                    }
                    theme={buttonTheme.iconGray}
                    className={classNames(!isHidden && "!text-gray-800")}
                    size={ButtonSize.medium}
                    onClick={() => {
                      if (isHidden) {
                        onUpdateViewOptions({
                          hiddenRecentAnswers: hiddenRecentAnswers.filter(
                            (id) => id !== questionId,
                          ),
                        });
                      } else {
                        onUpdateViewOptions({
                          hiddenRecentAnswers: [
                            ...hiddenRecentAnswers,
                            questionId,
                          ],
                        });
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TableColumnHider;
